import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import { Skeleton } from '../components/primitives';
import { Icon } from '../utils/icons';
import { isVideoPath } from '../utils/imageUrl';
import { formatPrice } from '../utils/format';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SectionRenderer from '../components/sections/SectionRenderer';
import { HOME_SECTION_REGISTRY } from '../components/sections/homeSectionRegistry';
import { applyLegacyThemeSettings, buildLegacyHomeLayout } from '../utils/homeLayout';
import { applyTheme } from '../utils/theme';
import { fetchFacets, ageGroupParam } from '../utils/catalog';
import './Home.css';

/**
 * Picks an image src for the hero collage.
 *
 * Skips video-only entries (which can't render in an <img>) and falls
 * through to the rest of the gallery so a product whose hero image is
 * a `*_1.mp4` doesn't leave us with a blank tile.
 */
const pickHeroImage = (product) => {
  const images = product?.images || [];
  return images.find((src) => src && !isVideoPath(src)) || null;
};

/**
 * Trust strip candidates, in order of what shoppers ask first. Each one
 * only shows when the store's settings make it true (free shipping needs
 * a threshold, pay on delivery needs COD switched on, and so on); the
 * strip shows the first four that apply.
 */
const TRUST_ITEMS = [
  { id: 'shipping', icon: Icon.Shipping },
  { id: 'cod',      icon: Icon.Cash },
  { id: 'delivery', icon: Icon.Clock },
  { id: 'returns',  icon: Icon.Returns },
  { id: 'support',  icon: Icon.Chat },
  { id: 'secure',   icon: Icon.Lock },
];

const Home = () => {
  const { t, i18n } = useTranslation();
  const { siteSettings } = useCart();
  const themeSettings = useSiteSettings();
  const {
    currency, currencySymbol, returnsEnabled, returnsWindowDays, storeName,
    featureAgeRecommendations, storeEmail, storePhone, whatsAppEnabled,
  } = themeSettings;
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [facets, setFacets] = useState(null);

  // Trust strip values are sourced from live admin settings so the
  // homepage never advertises a stale threshold or a returns policy
  // that's been turned off. The shipping badge hides itself when no
  // threshold is set ("0" = never free), and the returns badge mirrors
  // the same opt-in gate every other returns surface uses.
  const freeShipThreshold = parseFloat(siteSettings?.freeShippingThreshold) || 0;
  const currencyLabel = currencySymbol || currency || t('common.currency', 'USD');

  const deliveryMin = parseInt(siteSettings?.estimatedDeliveryDaysMin, 10) || 0;
  const deliveryMax = parseInt(siteSettings?.estimatedDeliveryDaysMax, 10) || 0;
  const trustItems = TRUST_ITEMS.filter(({ id }) => {
    if (id === 'shipping') return freeShipThreshold > 0;
    if (id === 'cod') return !!paymentConfig?.codEnabled;
    if (id === 'delivery') return deliveryMax > 0;
    if (id === 'returns') return !!returnsEnabled;
    if (id === 'support') return !!(whatsAppEnabled || storePhone || storeEmail);
    return true;
  }).slice(0, 4);

  // Per-badge interpolation values for the dynamic subtitles/titles.
  const trustVars = (id) => {
    if (id === 'shipping') {
      return { amount: formatPrice(freeShipThreshold, { lang: i18n.language }), currency: currencyLabel };
    }
    if (id === 'returns') {
      return { days: returnsWindowDays };
    }
    if (id === 'delivery') {
      return { min: deliveryMin || deliveryMax, max: deliveryMax, count: deliveryMax };
    }
    return {};
  };

  const [popularProducts, setPopularProducts] = useState([]);
  const [featuredProductsData, setFeaturedProductsData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [homeLayout, setHomeLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [openFaqKey, setOpenFaqKey] = useState(null);
  const previewToken = new URLSearchParams(window.location.search).get('themePreviewToken');
  const isEditorPreview = new URLSearchParams(window.location.search).get('themePreview') === '1';

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        // `featured` is the operator-curated lever for the hero
        // collage (the highest-value real estate on the home page).
        // `popular` is the algorithm-driven discovery surface — it
        // populates the "Popular Right Now" grid below the hero.
        // The fetches run in parallel so the home page boot stays a
        // single network round-trip wide.
        api.get('/api/settings/payment').then((res) => { if (!cancelled) setPaymentConfig(res.data); }).catch(() => {});
        fetchFacets().then((data) => { if (!cancelled) setFacets(data); }).catch(() => {});
        const [featuredRes, popularRes, catsRes, bundlesRes, layoutRes] = await Promise.all([
          api.get('/api/products?featured=true&limit=12').catch(() => ({ data: { products: [] } })),
          api.get('/api/products?popular=true&limit=20'),
          api.get('/api/categories', { params: { lang: i18n.language } }),
          api.get('/api/bundles/active').catch(() => ({ data: [] })),
          (previewToken
            ? api.get(`/api/theme-previews/${previewToken}/templates/home`)
            : api.get('/api/themes/templates/home')
          ).catch(() => ({ data: null })),
        ]);

        let popular = popularRes.data.products || [];
        if (popular.length === 0) {
          const fallback = await api.get('/api/products?limit=12&sortBy=price_asc');
          popular = fallback.data.products || [];
        }

        if (!cancelled) {
          setFeaturedProductsData(featuredRes.data.products || []);
          setPopularProducts(popular);
          setCategories(catsRes.data.categories || []);
          setBundles(Array.isArray(bundlesRes.data) ? bundlesRes.data : []);
          setHomeLayout(layoutRes.data);
        }
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, previewToken]);

  useEffect(() => {
    if (!homeLayout?.theme?.designTokens) return;
    applyTheme(homeLayout.theme.designTokens);
  }, [homeLayout]);

  // Pull the "recently viewed" strip from the same localStorage key
  // populated by the PDP. Single source of truth, no extra fetch.
  // Threshold of 3+ items keeps the section from looking sparse on
  // first-time visitors (who'd otherwise see a single tile).
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
      if (Array.isArray(stored) && stored.length >= 3) {
        setRecentlyViewed(stored.slice(0, 6));
      }
    } catch {
      // Malformed JSON — quietly ignore so the section just hides.
    }
  }, []);

  useEffect(() => {
    if (!isEditorPreview) return undefined;
    const handleLayoutPreview = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'STOREFRONT_LAYOUT_PREVIEW') return;
      setHomeLayout(event.data.payload);
    };
    window.addEventListener('message', handleLayoutPreview);
    return () => window.removeEventListener('message', handleLayoutPreview);
  }, [isEditorPreview]);

  if (loading) {
    // Calm typography-aware skeleton so the layout doesn't shift
    // when real content arrives. Replaces the legacy spinner; matches
    // the home grid's column count at each breakpoint via the same
    // .home-popular-grid styles consumed by the real render.
    return (
      <div className="home-page" aria-busy="true">
        <section className="home-hero" aria-hidden="true">
          <div className="container">
            <div className="home-hero__inner">
              <div className="home-hero__text">
                <Skeleton.Line width="40%" />
                <Skeleton.Block height="3.5rem" rounded="md" style={{ marginTop: 'var(--space-3)', maxWidth: '420px' }} />
                <Skeleton.Block height="2.5rem" rounded="md" style={{ marginTop: 'var(--space-2)', maxWidth: '320px' }} />
                <Skeleton.Line width="70%" style={{ marginTop: 'var(--space-3)' }} />
                <Skeleton.Line width="55%" />
                <Skeleton.Block height="48px" width="180px" rounded="pill" style={{ marginTop: 'var(--space-4)' }} />
              </div>
            </div>
          </div>
        </section>
        <section className="home-section">
          <div className="container">
            <div className="home-popular-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton.Card key={i} />
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Hero stage: operator-curated `featured` products first, then the
  // popular ones, deduped, each with a real photo (not a video file).
  const seenIds = new Set();
  const candidatePool = [...featuredProductsData, ...popularProducts].filter((p) => {
    if (!p || seenIds.has(p.id)) return false;
    seenIds.add(p.id);
    return true;
  });
  const heroItems = candidatePool
    .map((p) => ({ product: p, image: pickHeroImage(p) }))
    .filter((c) => Boolean(c.image))
    .slice(0, 6);

  const featuredProducts = popularProducts.length > 0 ? popularProducts : candidatePool;

  // One photo per age tile and per category tile, from products already
  // loaded, never repeating a photo between tiles.
  const usedImages = new Set();
  const takeImage = (match) => {
    const hit = candidatePool.find((p) => match(p) && pickHeroImage(p) && !usedImages.has(pickHeroImage(p)));
    if (!hit) return null;
    usedImages.add(pickHeroImage(hit));
    return pickHeroImage(hit);
  };
  const ageGroups = facets?.ageGroups || [];
  const ageTileImages = {};
  ageGroups.forEach((group) => {
    ageTileImages[ageGroupParam(group)] = takeImage((p) => p.ageMin != null && p.ageMin >= group.min && p.ageMin <= group.max)
      || takeImage((p) => (p.ageMax == null || p.ageMax >= group.min) && (p.ageMin == null || p.ageMin <= group.max));
  });
  const categoryImages = {};
  categories.forEach((category) => {
    const inCategory = (p) => p.category?.slug === category.slug || p.category?.id === category.id;
    categoryImages[category.slug] = [takeImage(inCategory), takeImage(inCategory), takeImage(inCategory)].filter(Boolean);
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) return;
    setNewsletterSubmitting(true);
    setNewsletterMessage('');
    try {
      await api.post('/api/newsletter/subscribe', {
        email,
        source: 'home',
        language: i18n.language || 'en',
      });
      setNewsletterMessage(t('home.newsletter.success', 'Thanks — you are on the list.'));
      setNewsletterEmail('');
    } catch (err) {
      const code = err?.response?.status;
      if (code === 400) {
        setNewsletterMessage(t('home.newsletter.invalid', 'Please use a valid email address.'));
      } else if (code === 429) {
        setNewsletterMessage(t('home.newsletter.tooMany', 'Too many attempts. Please try again shortly.'));
      } else {
        setNewsletterMessage(t('home.newsletter.error', 'Could not subscribe. Please try again later.'));
      }
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const effectiveLayout = applyLegacyThemeSettings(homeLayout || buildLegacyHomeLayout(themeSettings), themeSettings);
  const heroBase = (effectiveLayout?.template?.sections || []).find((section) => section.type === 'hero')?.settings || {};
  const heroSettings = { ...heroBase, ...(heroBase.translations?.[String(i18n.language).slice(0, 2)] || {}) };
  const sectionContext = {
    t,
    i18n,
    currency,
    storeName,
    trustItems,
    trustVars,
    heroItems,
    currencyLabel,
    featureAgeRecommendations,
    ageGroups,
    ageTileImages,
    categoryImages,
    featuredProducts,
    categories,
    bundles,
    recentlyViewed,
    openFaqKey,
    setOpenFaqKey,
    newsletterEmail,
    setNewsletterEmail,
    newsletterMessage,
    newsletterSubmitting,
    handleNewsletterSubmit,
    isEditorPreview,
    dynamicSources: {
      store: {
        name: themeSettings.storeName,
        email: themeSettings.storeEmail,
        phone: themeSettings.storePhone,
        address: themeSettings.storeAddress,
        nipt: themeSettings.storeNipt,
        whatsAppPhone: themeSettings.whatsAppPhone,
        currency: themeSettings.currency,
      },
    },
    contact: {
      email: themeSettings.storeEmail,
      phone: themeSettings.storePhone,
      address: themeSettings.storeAddress,
      nipt: themeSettings.storeNipt,
      whatsAppEnabled: themeSettings.whatsAppEnabled,
      whatsAppPhone: themeSettings.whatsAppPhone,
      mapLat: themeSettings.storeMapLat,
      mapLng: themeSettings.storeMapLng,
    },
  };

  return (
    <div className="home">
      <SEO
        title={heroSettings.title || t('home.hero.title', 'Quality products, delivered')}
        description={heroSettings.subtitle || t('home.hero.subtitle')}
        keywords={['online store', 'shop', 'products']}
      />

      <SectionRenderer layout={effectiveLayout} registry={HOME_SECTION_REGISTRY} context={sectionContext} />
    </div>
  );
};

export default Home;
